import { GOOGLE, NEARMAP } from '@/data/Imagery/Imagery'
import { clone } from '@/utilities/geospatial'
import { unVal } from '@/utilities/util'
import type { DataConfig, Layer } from '@/containers/Toc/context/types'

interface UserAccount {
  id: number | string
  nearmap?: string
}

interface UserWithAccounts {
  accountID: number | string
  accounts: UserAccount[]
}

const getLayers = (
  userLayers: unknown[],
  masterCommonLayerList: Record<string, Layer>
): Layer[] => {
  const layers: Layer[] = []
  userLayers.forEach(layerObj => {
    if (typeof layerObj === 'string' && layerObj in masterCommonLayerList) {
      layers.push(masterCommonLayerList[layerObj])
    } else if (layerObj !== null && typeof layerObj === 'object') {
      const layer = layerObj as Layer
      if (layer.toc && layer.toc.type !== 'mam') {
        layer.toc.userLayer = true
      }
      layers.push(layer)
    }
    // else: invalid layer (string but not found). Do nothing here.
  })
  return layers
}

const processUserLayers = (
  userLayers: unknown[],
  masterCommonLayerList: Record<string, Layer>
): Layer[] => {
  let layers = getLayers(userLayers, masterCommonLayerList)

  const layerLookupById = new Map<string, Layer>()
  const layerLookupByTocId = new Map<string, Layer>()
  layers.forEach(layer => {
    if (layer.id) layerLookupById.set(layer.id, layer)
    if (layer.toc?.id) layerLookupByTocId.set(layer.toc.id, layer)
  })

  const groups = layers.filter(layer => layer.children)

  groups.forEach(group => {
    const layerList: string[] = []
    group.children!.groupIds.forEach(id => layerList.push(id))
    group.children!.layerIds.forEach(id => layerList.push(id))

    let newOrder: Layer[] = []
    if (group.children!.order?.length) {
      let unprocessedChildren = layerList
      if (layerList.length) {
        group.children!.order.forEach(orderID => {
          if (layerList.includes(orderID)) {
            unprocessedChildren = unVal(unprocessedChildren, orderID)
            const viewObj =
              layerLookupById.get(orderID) ?? layerLookupByTocId.get(orderID)
            if (viewObj) newOrder.push(viewObj)
          }
        })
      }

      if (unprocessedChildren.length) {
        unprocessedChildren.forEach(unID => {
          const viewObj =
            layerLookupById.get(unID) ?? layerLookupByTocId.get(unID)
          if (viewObj) newOrder.push(viewObj)
        })
      }
    } else {
      layerList.forEach(layerID => {
        const viewObj =
          layerLookupById.get(layerID) ?? layerLookupByTocId.get(layerID)
        if (viewObj) newOrder.push(viewObj)
      })
    }
    newOrder = newOrder.filter(el => el != null)
    group.children!.items = newOrder

    const itemsToRemove = new Set<string>()
    group.children!.items.forEach(child => {
      // Handle nested groups
      if (child.children) {
        if (child.groupId) itemsToRemove.add(child.groupId)
        if (child.id) itemsToRemove.add(child.id)
      } else {
        // Handle layers (including Common Layers)
        if (child.toc?.id) itemsToRemove.add(child.toc.id)
        if (child.id) itemsToRemove.add(child.id)
      }
    })

    layers = layers.filter(layer => {
      if (layer.groupId && itemsToRemove.has(layer.groupId)) return false
      if (layer.id && itemsToRemove.has(layer.id)) return false
      if (layer.toc?.id && itemsToRemove.has(layer.toc.id)) return false
      return true
    })

    layerLookupById.clear()
    layerLookupByTocId.clear()
    layers.forEach(layer => {
      if (layer.id) layerLookupById.set(layer.id, layer)
      if (layer.toc?.id) layerLookupByTocId.set(layer.toc.id, layer)
    })
  })

  return layers
}

export const buildUserLayersArray = (
  userLayers: unknown,
  masterCommonLayerList: Record<string, Layer>
): Layer[] => {
  if (typeof userLayers !== 'object' || !Array.isArray(userLayers)) {
    return Object.values(masterCommonLayerList)
  }
  return processUserLayers(userLayers, masterCommonLayerList)
}

export const getImageryLayers = (user: UserWithAccounts): Layer[] => {
  const imagery: Layer[] = [GOOGLE]
  const activeAccount = user.accounts.filter(
    account => account.id === user.accountID
  )
  if (activeAccount[0]?.nearmap && activeAccount[0].nearmap !== '') {
    const nearMapClone = clone(NEARMAP)
    let nmURL = nearMapClone.sourcesArray[0].tiles[0]
    nmURL = `${nmURL}?apikey=${activeAccount[0].nearmap}`
    nearMapClone.sourcesArray[0].tiles[0] = nmURL
    imagery.push(nearMapClone)
  }
  return imagery
}

interface OrderLayer {
  layer: string
}
interface OrderGroup {
  group: string
  children?: Array<OrderLayer | OrderGroup>
}
type OrderEntry = OrderLayer | OrderGroup

export const getOrderFromDataConfig = (
  dataConfig: Layer[],
  deleteId: string | null = null
): OrderEntry[] => {
  // create an order array to pass to the order api
  // Order is an array of objects following the below example
  /*
  [
    {layer: "layer_layer_8"},
    {layer: "layer_layer_2"},
    {
      group: "GgAkjLkgJk",
      children: [{layer: "layer_layer_3"},{group: "LkPoKgAsFD"}],
    },
    {
      group: "LkPoKgAsFD",
      children: [{layer: "layer_layer_5"}],
    }
  ]
  */
  const config: Layer[] = clone(dataConfig)
  const newOrder: OrderEntry[] = []
  const processConfigChildren = (configObj: Layer): void => {
    if (configObj.children) {
      if (deleteId) {
        const groupsIndex = (configObj.children.items ?? []).findIndex(
          group => group && group.groupId === deleteId
        )
        const groupIdsIndex = configObj.children.groupIds.findIndex(
          groupId => groupId && groupId === deleteId
        )
        if (groupsIndex >= 0) {
          configObj.children.items!.splice(groupsIndex, 1)
        }
        if (groupIdsIndex >= 0) {
          configObj.children.groupIds.splice(groupIdsIndex, 1)
        }
      }
      ;(configObj.children.items ?? []).forEach(item => {
        if (item.children) {
          processConfigChildren(item)
        } else if (item.toc?.id) {
          newOrder.push({ layer: item.toc.id })
        }
      })

      const children: OrderEntry[] = (configObj.children.items ?? []).map(
        item => {
          if (item.children) {
            return { group: item.groupId! }
          }
          return { layer: item.toc!.id }
        }
      )

      newOrder.push({
        group: configObj.groupId!,
        children,
      })
    } else if (configObj.toc?.id) {
      newOrder.push({ layer: configObj.toc.id })
    }
  }
  config.forEach(configObj => {
    if (deleteId && configObj.groupId && configObj.groupId === deleteId) return
    processConfigChildren(configObj)
  })
  return newOrder
}

export const getLayerConfigUsingTocId = (
  dataConfig: DataConfig | null | undefined,
  layerId: string
): Layer | null => {
  let targetLayer: Layer | null = null

  if (!dataConfig?.tocLayers) {
    return targetLayer
  }

  const processLayer = (layer: Layer): void => {
    if (layer.toc?.id === layerId) {
      targetLayer = layer
    }
    if (layer.children?.items) {
      layer.children.items.forEach(item => processLayer(item))
    }
  }

  dataConfig.tocLayers.forEach(layer => processLayer(layer))
  return targetLayer
}

export const getCommonLayerConfigUsingLayerId = (
  dataConfig: DataConfig,
  layerId: string
): Layer | null => {
  let targetLayer: Layer | null = null
  const processLayer = (layer: Layer): void => {
    if (layer.layersArray) {
      const targetLayerArray = layer.layersArray.filter(
        layerObj => layerObj.layer.id === layerId
      )
      if (targetLayerArray.length) targetLayer = layer
    }
    if (layer.children?.items) {
      layer.children.items.forEach(item => processLayer(item))
    }
  }

  dataConfig.tocLayers.forEach(layer => processLayer(layer))
  return targetLayer
}

export const getLayerConfigUsingLayerId = (
  dataConfig: DataConfig,
  layerId: string
): Layer | null => {
  let targetLayer: Layer | null = null
  const processLayer = (layer: Layer): void => {
    if (layer.layersArray && layer.layersArray[0]?.layer.id === layerId) {
      targetLayer = layer
    }
    if (layer.children?.items) {
      layer.children.items.forEach(item => processLayer(item))
    }
  }

  dataConfig.tocLayers.forEach(layer => processLayer(layer))
  if (!targetLayer) {
    dataConfig.auxiliaryLayers.forEach(layer => processLayer(layer))
  }
  return targetLayer
}

interface StyleLayer {
  id: string
  layout?: { visibility?: string }
}
interface StyleLike {
  layers: StyleLayer[]
}

export const getVisibleLayersFromDataConfig = (
  tocLayers: Layer[],
  style: StyleLike
): Layer[] => {
  const visibleLayers: Layer[] = []
  const processTocObj = (tocObj: Layer, styleLayer: StyleLayer): void => {
    if (tocObj.layersArray) {
      tocObj.layersArray.forEach(layerObject => {
        if (layerObject.layer.id === styleLayer.id) {
          visibleLayers.push(tocObj)
        }
      })
    }
    if (tocObj.children?.items) {
      tocObj.children.items.forEach(item => {
        if (item.layersArray) {
          item.layersArray.forEach(layerObject => {
            if (layerObject.layer.id === styleLayer.id) {
              visibleLayers.push(item)
            }
          })
        } else {
          processTocObj(item, styleLayer)
        }
      })
    }
  }

  style.layers.forEach(styleLayer => {
    if (!styleLayer.layout) return
    if (styleLayer.layout.visibility === 'none') return
    tocLayers.forEach(tocObject => {
      processTocObj(tocObject, styleLayer)
    })
  })
  return visibleLayers
}

export const getAllLayersFromDataConfig = (
  tocLayers: Layer[]
): StyleLayer[] => {
  const layers: StyleLayer[] = []
  const processTocObj = (tocObj: Layer): void => {
    if (tocObj.layersArray) {
      tocObj.layersArray.forEach(layerObject => {
        if (layerObject.layer.id) {
          layers.push(layerObject.layer)
        }
      })
    }
    if (tocObj.children?.items) {
      tocObj.children.items.forEach(childObj => {
        if (childObj.children) {
          processTocObj(childObj)
        } else if (childObj.layersArray) {
          childObj.layersArray.forEach(layerObject => {
            if (layerObject.layer.id) {
              layers.push(layerObject.layer)
            }
          })
        }
      })
    }
  }

  tocLayers.forEach(tocObject => processTocObj(tocObject))
  return layers
}
